<template>
  <VueJsModalPrimary
    v-bind="getModalAttrs"
    @before-close="beforeClose"
    @before-open="beforeOpen"
  >
    <div :style="style.container">
      <div :style="style.headerContainer">
        <HeaderLargePrimary>
           {{ headerText }}
        </HeaderLargePrimary>
        <TextButtonSmallMuted
          v-if="!edit && !guidedMode"
          @click="switchGuidedMode(true)"
        >
          Use guided mode
        </TextButtonSmallMuted>
        <TextButtonSmallMuted
          v-else-if="(
            !edit &&
            guidedMode &&
            guidedModeStep !== lastGuidedModeStep
          )"
          @click="switchGuidedMode(false)"
        >
          Use simple mode
        </TextButtonSmallMuted>
      </div>
      <div
        v-if="guidedMode"
        :style="style.innerContainer"
      >
        <FormTenantUserSkillSecondary
          :disableInputSkillName="disableInputSkillName"
          :form.sync="form"
          :step="guidedModeStep"
          :tenantUserSkills="tenantUserSkills"
          @step-forward="stepForwardGuidedMode"
        />
        <div :style="style.footerContainer">
          <ButtonSecondary
            v-if="guidedModeStep !== lastGuidedModeStep"
            @click="close"
          >
            Cancel
          </ButtonSecondary>
          <ButtonPrimary
            v-if="guidedModeStep === 0"
            :disabled="form.data.skillName.trim().length === 0"
            @click="stepForwardGuidedMode"
          >
            Next
          </ButtonPrimary>
          <ButtonPrimary
            v-else-if="guidedModeStep === lastGuidedModeStep"
            @click="close"
          >
            Done
          </ButtonPrimary>
          <div
            v-else-if="guidedModeStep === lastGuidedModeStep - 1"
            :style="style.buttonsContainer"
          >
            <ButtonSecondary @click="stepBackwardGuidedMode">
              Back
            </ButtonSecondary>
            <ButtonPrimary
              @click="createTenantUserSkill"
              :style="style.button"
            >
              Confirm
            </ButtonPrimary>
          </div>
          <div
            v-else
            :style="style.buttonsContainer"
          >
            <ButtonSecondary
              @click="stepBackwardGuidedMode"
            >
              Back
            </ButtonSecondary>
            <ButtonPrimary
              @click="stepForwardGuidedMode"
              :style="style.button"
            >
              Skip
            </ButtonPrimary>
          </div>
        </div>
      </div>
      <div
        v-else
        :style="style.innerContainer"
      >
        <div :style="style.dummyContainer" />
        <div :style="style.formsContainer">
          <FormTenantUserSkillPrimary
            :form.sync="form"
            :disableInputSkillName="disableInputSkillName || edit"
            :tenantUserSkills="tenantUserSkills"
          />
        </div>
        <div :style="style.footerContainer">
          <template v-if="edit">
            <ButtonWarning @click="deleteTenantUserSkill">
              Delete
            </ButtonWarning>
            <div :style="style.buttonsContainer">
              <ButtonSecondary @click="close">
                Cancel
              </ButtonSecondary>
              <ButtonPrimary
                :style="style.button"
                @click="updateTenantUserSkill"
              >
                Save
              </ButtonPrimary>
            </div>
          </template>
          <template v-else>
            <div :style="style.dummyContainer" />
            <div :style="style.buttonsContainer">
              <ButtonSecondary @click="close">
                Cancel
              </ButtonSecondary>
              <ButtonPrimary
                :disabled="disableCreateButton"
                :style="style.button"
                @click="createTenantUserSkill"
              >
                Save
              </ButtonPrimary>
            </div>
          </template>
        </div>
      </div>
    </div>
  </VueJsModalPrimary>
</template>

<script>
  import ButtonPrimary from '@modules/v2/button/button-primary'
  import ButtonSecondary from '@modules/v2/button/button-secondary'
  import ButtonWarning from '@modules/v2/button/button-warning'
  import FormTenantUserSkillPrimary
    from '@modules/v2/form/form-tenant-user-skill-primary'
  import FormTenantUserSkillSecondary
    from '@modules/v2/form/form-tenant-user-skill-secondary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import TextButtonSmallMuted from '@modules/v2/button/text-button-small-muted'
  import VueJsModalPrimary from '@modules/v2/modal/vue-js-modal-primary'
  import { FormTenantUserSkill } from '@js/forms/tenant-user-skill'
  import { isString } from '@js/data-utils'
  import { Layout } from '@js/styles'
  import { MODAL_WINDOW_NAMES } from '@js/constants'
  import { normalizeSkillName } from '@js/str-utils'
  import { stepToStepIndex } from '@modules/v2/form/form-tenant-user-skill-secondary'

  export default {
    name: 'ModalTenantUserSkillEditPrimary',
    components: {
      ButtonPrimary,
      ButtonSecondary,
      ButtonWarning,
      FormTenantUserSkillPrimary,
      FormTenantUserSkillSecondary,
      HeaderLargePrimary,
      TextButtonSmallMuted,
      VueJsModalPrimary,
    },
    data: function () {
      return {
        disableInputSkillName: false,
        form: new FormTenantUserSkill(),
        edit: false,
        guidedMode: false,
        guidedModeStep: 0,
        name: MODAL_WINDOW_NAMES.CONFIGURE_TENANT_USER_SKILL_PRIMARY,
        payload: null,
        tenantUserSkills: [],
        style: {
          button: {
            margin: `0 0 0 ${Layout.space.md}`,
          },
          buttonsContainer: {
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'flex-end',
            height: '100%',
          },
          container: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            padding: Layout.space.lg,
            width: '100%',
          },
          dummyContainer: {
            height: '0px',
          },
          footerContainer: {
            alignItems: 'flex-end',
            display: 'flex',
            height: '32px',
            justifyContent: 'space-between',
            width: '100%',
          },
          formsContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          },
          headerContainer: {
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          },
          innerContainer: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
          },
        },
      };
    },
    computed: {
      disableCreateButton: function () {
        return normalizeSkillName(this.form.data.skillName).length === 0;
      },
      getModalAttrs: function () {
        return {
          height: this.guidedMode ? '574px' : '478px',
          maxHeight: this.guidedMode ? 574 : 478,
          maxWidth: 480,
          minHeight: this.guidedMode ? 574 : 478,
          name: this.name,
        };
      },
      headerText: function () {
        return this.edit ? 'Edit Skill' : 'Add Skill';
      },
      lastGuidedModeStep: function () {
        return Object.keys(stepToStepIndex).length - 1;
      },
    },
    methods: {
      beforeClose: function () {
        this.clearCache();
      },
      beforeOpen: function (e) {
        if (
          e.params === undefined ||
          e.params === null
        ) {
          this.$store.commit('notification/setWarning', 'Failed to load skills');
          this.$modal.hide(this.name);
        }

        if (
          e.params.tenantUserSkill !== undefined &&
          e.params.tenantUserSkill !== null
        ) {
          this.edit = true;
          this.form.set(e.params.tenantUserSkill);
        }

        if (
          isString(e.params.skillName) &&
          e.params.skillName.trim().length > 0
        ) {
          this.disableInputSkillName = true;
          this.form.data.skillName = e.params.skillName;
        }

        if (
          e.params.tenantUserSkills !== undefined &&
          e.params.tenantUserSkills !== null
        ) {
          this.tenantUserSkills = e.params.tenantUserSkills;
        }

        if (
          e.params.guidedMode !== undefined &&
          e.params.guidedMode !== null
        ) {
          this.guidedMode = e.params.guidedMode;
        }

        if (
          e.params.payload !== undefined &&
          e.params.payload !== null
        ) {
          this.payload = e.params.payload;
        }
      },
      clearCache: function () {
        this.disableInputSkillName = false;
        this.form.clear();
        this.edit = false;
        this.guidedMode = false;
        this.guidedModeStep = 0;
      },
      close: function () {
        this.$modal.hide(this.name);
      },
      createTenantUserSkill: function () {
        this.$emit('create-skill', this.form, this.payload);
      },
      deleteTenantUserSkill: function () {
        this.$emit('delete-skill', this.form, this.payload);
      },
      updateTenantUserSkill: function () {
        this.$emit('update-skill', this.form, this.payload);
      },
      stepBackwardGuidedMode: function () {
        this.guidedModeStep = Math.max(
          0, this.guidedModeStep - 1
        );
      },
      stepForwardGuidedMode: function () {
        this.guidedModeStep = Math.min(
          this.guidedModeStep + 1, this.lastGuidedModeStep
        );
      },
      switchGuidedMode: function (guidedMode) {
        this.guidedMode = guidedMode;
        this.$emit('switch-mode', this.guidedMode);
      },
    },
    watch: {
      guidedMode: function (newVal, oldVal) {
        if (newVal) {
          this.guidedModeStep = 0;
        }
      },
    },
  };
</script>
